import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import NextLink, { LinkProps } from 'next/link'
import { NextRouter, useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useRouter as useNprogressRouter } from 'next-nprogress-bar'
import { forwardRef, HTMLAttributeAnchorTarget, PropsWithChildren } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { getUpdatedLocaleForURLAfterRegionSwitch as getUpdatedLocaleForURL } from '@src/components/locale/utils'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'

interface ExtendedLinkProps extends LinkProps {
  target?: HTMLAttributeAnchorTarget
  'aria-label'?: string
}

const LinkI18N = forwardRef<HTMLAnchorElement, PropsWithChildren<ExtendedLinkProps>>(function LinkI18N(
  { onClick, href, target, children, 'aria-label': ariaLabel, ...restProps },
  ref,
) {
  const { t } = useTranslation('common')
  const { locale, regionDetails } = useLocaleContext()
  const updatedLocaleForURL = getUpdatedLocaleForURL(locale, regionDetails)
  const childrenText = typeof children === 'string' ? children : t('link')
  const computedAriaLabel =
    ariaLabel ?? (target === '_blank' ? t('opens_in_new_tab', { linkText: childrenText }) : undefined)

  return (
    <NextLink
      {...restProps}
      ref={ref}
      href={href}
      prefetch={false}
      onClick={onClick}
      locale={updatedLocaleForURL}
      target={target}
      aria-label={computedAriaLabel}
    >
      {children}
    </NextLink>
  )
})

const LinkWithoutI18n = forwardRef<HTMLAnchorElement, PropsWithChildren<ExtendedLinkProps>>(function LinkWithoutI18n(
  { href, target, children, 'aria-label': ariaLabel, ...restProps },
  ref,
) {
  const { t } = useTranslation('common')
  const childrenText = typeof children === 'string' ? children : t('link')
  const computedAriaLabel =
    ariaLabel ?? (target === '_blank' ? t('opens_in_new_tab', { linkText: childrenText }) : undefined)

  return (
    <NextLink ref={ref} href={href} {...restProps} prefetch={false} target={target} aria-label={computedAriaLabel}>
      {children}
    </NextLink>
  )
})

export const Link = forwardRef<HTMLAnchorElement, PropsWithChildren<ExtendedLinkProps>>((props, ref) => {
  const isI18nPreviewEnabled = useI18nPreviewContext()
  return isI18nPreviewEnabled ? <LinkI18N {...props} ref={ref} /> : <LinkWithoutI18n {...props} ref={ref} />
})

// @i18n-ignore
Link.displayName = 'CustomLink'

export const useQuinceNprogressRouter = () => {
  const router = useNprogressRouter()
  const push = (to: string, options?: NavigateOptions) => {
    router.push(to, options)
  }

  const replace = (to: string, options?: NavigateOptions) => {
    router.replace(to, options)
  }

  return {
    ...router,
    push,
    replace,
  }
}

export interface QuinceRouter extends Omit<NextRouter, 'push' | 'replace'> {
  push: (to: string, options?: { shallow?: boolean; scroll?: boolean; locale?: string | false }) => void
  replace: (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean; locale?: string | false }) => void
}

export const useQuinceRouter: () => QuinceRouter = () => {
  const router = useRouter()

  const push = (to: string, options?: { shallow?: boolean; scroll?: boolean }) => {
    void router.push(to, to, options)
  }

  const replace = (to: string, as?: string, options?: { shallow?: boolean; scroll?: boolean }) => {
    void router.replace(to, as ?? to, options) // as is optional in next/router
  }

  return {
    ...router,
    push,
    replace,
  }
}
